/**
 * useProactiveNudge — one-shot fetch of `/api/ai-cart/nudges` used by
 * the Sancta widget to open with a personalised greeting instead of a
 * generic hello.
 *
 * Behavior:
 *   - Fetches once per widget mount for a logged-in user.
 *   - Skips silently for guests or on API failure.
 *   - Dismissal is *per-tab* (sessionStorage), so a shopper who
 *     dismisses the greeting doesn't see it again until they reload
 *     the tab. Explicitly NOT localStorage so tomorrow's visit gets a
 *     fresh nudge.
 *   - Only surfaces nudges with `count > 0`; empty carts get the
 *     stock greeting.
 */
import * as React from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export interface AICartNudge {
  id: "planning" | "bought" | "postponed";
  status: "planning" | "bought" | "postponed";
  count: number;
  hint: string;
  cta: string;
  sample_titles?: string[];
}

const DISMISS_KEY = "sb_sancta_greeting_dismissed_v1";

function readDismissed(): string[] {
  try {
    const raw = sessionStorage.getItem(DISMISS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export function useProactiveNudge() {
  const { user } = useAuth();
  const [nudge, setNudge] = React.useState<AICartNudge | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [dismissed, setDismissed] = React.useState<string[]>(() => readDismissed());

  React.useEffect(() => {
    if (!user) { setNudge(null); return; }
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const r = await api.get<{ nudges: AICartNudge[] }>("/ai-cart/nudges");
        if (!alive) return;
        const all = r.data?.nudges || [];
        const skip = new Set(readDismissed());
        // Pick the highest-priority nudge that hasn't been dismissed
        // this tab. Priority order is already returned by the server:
        // planning → bought → postponed.
        const pick = all.find(n => !skip.has(n.id)) || null;
        setNudge(pick);
      } catch { /* guests / offline / new users */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [user]);

  const dismiss = React.useCallback(() => {
    if (!nudge) return;
    setDismissed(prev => {
      const next = Array.from(new Set([...prev, nudge.id]));
      try { sessionStorage.setItem(DISMISS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
    setNudge(null);
  }, [nudge]);

  return { nudge, loading, dismiss };
}
